import { useEffect, useState } from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';

import { getTimezoneByZipCode } from '../helpers';

const useProcedureTimeRestrictions = (
  procedureStartDate: moment.Moment | null,
  selectedDuration: number,
  skipTimeCheck: boolean,
  zipCode: string | number | null = null,
) => {
  const [timeToolTipOpen, setTimeToolTipOpen] = useState<boolean>(false);
  const [durationToolTipOpen, setDurationToolTipOpen] = useState<boolean>(false);
  const [startBetween10to12, setStartBetween10to12] = useState<boolean>(false);
  const [endBetween10to12, setEndBetween10to12] = useState<boolean>(false);
  const [isTZIdentified, setisTZIdentified] = useState<boolean>(false);
  const [zone, setZone] = useState<string | null>(null);

  const userTimezone = momentTz.tz.guess(true);
  const nowInUserTimezone = momentTz().tz(userTimezone);

  useEffect(() => {
    if (!zipCode) {
      setisTZIdentified(false);
      setZone(null);
    }
    const zone = getTimezoneByZipCode(`${zipCode}`);
    if (!zone) {
      setisTZIdentified(false);
      setZone(null);
    } else {
      setisTZIdentified(true);
      setZone(zone);
    }
  }, [zipCode]);

  useEffect(() => {
    if (!procedureStartDate || skipTimeCheck) return;

    const startTime = zone ? momentTz(procedureStartDate).tz(zone) : moment(procedureStartDate);
    const endTime = zone
      ? momentTz(startTime).add(selectedDuration, 'seconds').tz(zone)
      : moment(startTime).add(selectedDuration, 'seconds');

    const isWithinTimeRange = (
      date: moment.Moment,
      startHour: number,
      startMinute: number,
      endHour: number,
      endMinute: number,
    ) => {
      const startOfDay = moment(date).startOf('day').hours(startHour).minutes(startMinute);
      const endOfDay = moment(date).startOf('day').hours(endHour).minutes(endMinute);
      return (
        date.isBetween(startOfDay, endOfDay, null, '[]') ||
        date.isSame(startOfDay) ||
        date.isSame(endOfDay)
      );
    };

    const isTimeToolTipOpen = isWithinTimeRange(startTime, 0, 0, 3, 59); // Start time is b/w 12:00 AM to 3:59 AM, Starting at 4:00 AM is allowed
    const isDurationToolTipOpen = isWithinTimeRange(endTime, 0, 0, 4, 0) && !isTimeToolTipOpen; // End time is b/w 12:00 AM to 4:00 AM, Ending at exactly 12:00 AM is allowed

    const startBetween10to12 =
      isWithinTimeRange(startTime, 22, 0, 24, 0) && !isTimeToolTipOpen && !isDurationToolTipOpen; // End time is b/w 10:00 PM to 12:00 AM
    const endBetween10to12 =
      isWithinTimeRange(endTime, 22, 0, 24, 0) &&
      !isTimeToolTipOpen &&
      !isDurationToolTipOpen &&
      !startBetween10to12; // End time is b/w 10:00 PM to 12:00 AM, Ending at exactly 12:00 AM is allowed

    setTimeToolTipOpen(isTimeToolTipOpen);
    setDurationToolTipOpen(isDurationToolTipOpen);
    setStartBetween10to12(startBetween10to12);
    setEndBetween10to12(endBetween10to12);
  }, [procedureStartDate, selectedDuration, skipTimeCheck, zone]);

  return {
    timeToolTipOpen,
    durationToolTipOpen,
    startBetween10to12,
    endBetween10to12,
    abbreviatedZone: zone ? momentTz().tz(zone).format('z') : nowInUserTimezone.format('z'),
    zone: zone ? zone : userTimezone,
    isTZIdentified,
  };
};

export default useProcedureTimeRestrictions;
